import type {
  FieldMappingState,
  FieldMappingStateEntry,
  GeometryAnalysis,
  ImportCoordinateColumns,
  ImportFieldDefinition,
  ImportFieldMapping,
  ImportPreviewIssue,
  ImportSourceMode,
  ImportSourceRow,
  ImportSourceSnapshot,
  ImportValueMapping,
  ImportValueMappingEntry,
  ImportValueMatcherDefinition,
  ParsedImportSource,
  SerializedImportableEntityDefinition,
  SourceColumnMeta,
  ValueMappingState,
} from './types';

const MAX_COLUMNS = 200;
const MAX_ROWS = 2000;

const LNG_COLUMN_HINTS = [
  'lng',
  'lon',
  'long',
  'longitude',
  'x',
  'coord_x',
  'coordx',
  'utm_x',
  'este',
];

const LAT_COLUMN_HINTS = [
  'lat',
  'latitude',
  'y',
  'coord_y',
  'coordy',
  'utm_y',
  'norte',
];

const TRUE_VALUES = ['true', '1', 'si', 'sí', 'yes', 'y', 'activo', 'active', 'verdadero'];
const FALSE_VALUES = ['false', '0', 'no', 'n', 'inactivo', 'inactive', 'falso'];

interface CsvParseResult {
  headers: string[];
  rows: string[][];
}

interface KmlRawRow {
  index: number;
  values: Record<string, unknown>;
  geometry?: Record<string, unknown>;
}

function normalizeText(value: unknown): string {
  return String(value ?? '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '');
}

function stringifyValue(value: unknown): string {
  if (value === null || value === undefined) {
    return '';
  }

  if (typeof value === 'string') {
    return value;
  }

  return String(value);
}

function isBlank(value: unknown): boolean {
  return stringifyValue(value).trim().length === 0;
}

async function readFileText(file: File): Promise<string> {
  if (typeof file.text === 'function') {
    return file.text();
  }

  return new Promise<string>((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result ?? ''));
    reader.onerror = () => reject(new Error('No se pudo leer el archivo localmente'));
    reader.readAsText(file);
  });
}

function parseCsvText(text: string): CsvParseResult {
  const cleaned = text.replace(/^\uFEFF/, '');
  const rows: string[][] = [];
  let currentRow: string[] = [];
  let currentCell = '';
  let inQuotes = false;

  for (let index = 0; index < cleaned.length; index += 1) {
    const char = cleaned[index];
    const next = cleaned[index + 1];

    if (inQuotes) {
      if (char === '"' && next === '"') {
        currentCell += '"';
        index += 1;
      } else if (char === '"') {
        inQuotes = false;
      } else {
        currentCell += char;
      }
      continue;
    }

    if (char === '"') {
      inQuotes = true;
      continue;
    }

    if (char === ',') {
      currentRow.push(currentCell);
      currentCell = '';
      continue;
    }

    if (char === '\n' || char === '\r') {
      if (char === '\r' && next === '\n') {
        index += 1;
      }
      currentRow.push(currentCell);
      rows.push(currentRow);
      currentRow = [];
      currentCell = '';
      continue;
    }

    currentCell += char;
  }

  if (currentCell.length > 0 || currentRow.length > 0) {
    currentRow.push(currentCell);
    rows.push(currentRow);
  }

  if (rows.length === 0) {
    throw new Error('El archivo CSV no contiene filas');
  }

  const [rawHeaders, ...rawRows] = rows;
  const headers = rawHeaders.map((header, index) => {
    const trimmed = header.trim();
    return trimmed.length > 0 ? trimmed : `column_${index + 1}`;
  });

  return {
    headers,
    rows: rawRows.filter((row) => row.some((value) => value.trim().length > 0)),
  };
}

function dedupeHeaders(headers: string[]): { headers: string[]; warnings: string[] } {
  const seen = new Map<string, number>();
  const warnings: string[] = [];

  const deduped = headers.map((header) => {
    const normalized = normalizeText(header) || 'column';
    const count = seen.get(normalized) ?? 0;
    seen.set(normalized, count + 1);
    if (count === 0) {
      return normalized;
    }

    const dedupedHeader = `${normalized}_${count + 1}`;
    warnings.push(`La columna "${header}" estaba duplicada. Se renombró a "${dedupedHeader}".`);
    return dedupedHeader;
  });

  return { headers: deduped, warnings };
}

function guessColumnType(values: unknown[]): SourceColumnMeta['guessedType'] {
  const nonEmpty = values
    .map((value) => stringifyValue(value).trim())
    .filter((value) => value.length > 0)
    .slice(0, 12);

  if (nonEmpty.length === 0) {
    return 'string';
  }

  if (
    nonEmpty.every((value) => {
      const normalized = normalizeText(value);
      return TRUE_VALUES.includes(normalized) || FALSE_VALUES.includes(normalized);
    })
  ) {
    return 'boolean';
  }

  if (
    nonEmpty.every((value) => /^-?\d+([.,]\d+)?$/.test(value))
  ) {
    return 'number';
  }

  if (
    nonEmpty.every((value) =>
      /^\d{4}-\d{2}-\d{2}/.test(value) ||
      /^\d{2}\/\d{2}\/\d{4}$/.test(value) ||
      /^\d{2}-\d{2}-\d{4}$/.test(value),
    )
  ) {
    return 'date';
  }

  return 'string';
}

function buildColumnMeta(
  headers: string[],
  rows: ImportSourceRow[],
): SourceColumnMeta[] {
  return headers.map((header) => {
    const values = rows.map((row) => row.values[header]);
    const sampleValues = values
      .map((value) => stringifyValue(value).trim())
      .filter((value) => value.length > 0)
      .slice(0, 3);

    return {
      key: header,
      label: header,
      sampleValues,
      guessedType: guessColumnType(values),
      emptyCount: values.filter((value) => isBlank(value)).length,
    };
  });
}

export function detectSuggestedCoordinateColumns(
  columns: Array<Pick<SourceColumnMeta, 'key'>>,
): ImportCoordinateColumns | null {
  const normalizedColumns = columns.map((column) => ({
    key: column.key,
    normalized: normalizeText(column.key),
  }));

  const lng = normalizedColumns.find((column) =>
    LNG_COLUMN_HINTS.some((hint) => column.normalized === hint || column.normalized.endsWith(`_${hint}`)),
  );
  const lat = normalizedColumns.find((column) =>
    LAT_COLUMN_HINTS.some((hint) => column.normalized === hint || column.normalized.endsWith(`_${hint}`)),
  );

  if (!lng || !lat) {
    return null;
  }

  return {
    lng: lng.key,
    lat: lat.key,
  };
}

function buildGeometryAnalysis(
  rows: ImportSourceRow[],
  columns: SourceColumnMeta[],
): GeometryAnalysis {
  const geometryKinds = new Set<string>();
  let pointGeometryRows = 0;
  let nonPointGeometryRows = 0;
  let missingGeometryRows = 0;

  for (const row of rows) {
    const geometry = row.geometry as { type?: string } | undefined;
    if (!geometry || typeof geometry.type !== 'string') {
      missingGeometryRows += 1;
      continue;
    }

    geometryKinds.add(geometry.type);
    if (geometry.type === 'Point') {
      pointGeometryRows += 1;
    } else {
      nonPointGeometryRows += 1;
    }
  }

  const featureGeometryAvailable = pointGeometryRows > 0 || nonPointGeometryRows > 0;
  const suggestedCoordinateColumns = detectSuggestedCoordinateColumns(columns);
  const availableModes: ImportSourceMode[] = [];

  if (featureGeometryAvailable) {
    availableModes.push('feature-geometry');
  }
  if (suggestedCoordinateColumns) {
    availableModes.push('coordinate-columns');
  }

  return {
    availableModes,
    featureGeometryAvailable,
    pointGeometryRows,
    nonPointGeometryRows,
    missingGeometryRows,
    geometryKinds: Array.from(geometryKinds),
    suggestedCoordinateColumns,
  };
}

function buildParsedSource(
  fileName: string,
  format: ParsedImportSource['format'],
  rows: ImportSourceRow[],
  headers: string[],
  warnings: string[],
): ParsedImportSource {
  const columnMeta = buildColumnMeta(headers, rows);

  return {
    fileName,
    format,
    rows,
    columns: columnMeta,
    warnings,
    geometryAnalysis: buildGeometryAnalysis(rows, columnMeta),
  };
}

export async function parseCsvImportFile(file: File): Promise<ParsedImportSource> {
  const rawText = await readFileText(file);
  const parsed = parseCsvText(rawText);
  const { headers, warnings: headerWarnings } = dedupeHeaders(parsed.headers);
  const warnings = [...headerWarnings];

  const limitedHeaders = headers.slice(0, MAX_COLUMNS);
  if (headers.length > MAX_COLUMNS) {
    warnings.push(`Se recortaron columnas a ${MAX_COLUMNS} para cumplir el límite del backend.`);
  }

  const limitedRows = parsed.rows.slice(0, MAX_ROWS);
  if (parsed.rows.length > MAX_ROWS) {
    warnings.push(`Se recortaron filas a ${MAX_ROWS}. El backend no acepta más filas por preview.`);
  }

  const rows: ImportSourceRow[] = limitedRows.map((rawRow, index) => {
    const values = limitedHeaders.reduce<Record<string, unknown>>((accumulator, header, headerIndex) => {
      accumulator[header] = rawRow[headerIndex]?.trim() ?? '';
      return accumulator;
    }, {});

    return {
      index,
      values,
    };
  });

  return buildParsedSource(file.name, 'csv', rows, limitedHeaders, warnings);
}

function extractGeoJsonFeatures(
  input: unknown,
): GeoJSON.Feature[] {
  if (
    typeof input === 'object' &&
    input !== null &&
    'type' in input &&
    (input as { type?: string }).type === 'FeatureCollection'
  ) {
    const collection = input as GeoJSON.FeatureCollection;
    return collection.features ?? [];
  }

  if (
    typeof input === 'object' &&
    input !== null &&
    'type' in input &&
    (input as { type?: string }).type === 'Feature'
  ) {
    return [input as GeoJSON.Feature];
  }

  if (Array.isArray(input)) {
    return input.filter(
      (item): item is GeoJSON.Feature =>
        typeof item === 'object' &&
        item !== null &&
        'type' in item &&
        (item as { type?: string }).type === 'Feature',
    );
  }

  throw new Error('El archivo GeoJSON no contiene un FeatureCollection válido');
}

export async function parseGeoJsonImportFile(file: File): Promise<ParsedImportSource> {
  const rawText = await readFileText(file);
  const rawJson = JSON.parse(rawText) as unknown;
  const features = extractGeoJsonFeatures(rawJson);
  const warnings: string[] = [];

  const limitedFeatures = features.slice(0, MAX_ROWS);
  if (features.length > MAX_ROWS) {
    warnings.push(`Se recortaron features a ${MAX_ROWS}. El backend no acepta más filas por preview.`);
  }

  const propertyKeySet = new Set<string>();
  for (const feature of limitedFeatures) {
    const properties = feature.properties ?? {};
    for (const key of Object.keys(properties)) {
      propertyKeySet.add(key);
      if (propertyKeySet.size >= MAX_COLUMNS) {
        break;
      }
    }
    if (propertyKeySet.size >= MAX_COLUMNS) {
      break;
    }
  }

  const headers = Array.from(propertyKeySet);
  if (propertyKeySet.size >= MAX_COLUMNS) {
    warnings.push(`Se recortaron columnas a ${MAX_COLUMNS} para cumplir el límite del backend.`);
  }

  const { headers: normalizedHeaders, warnings: headerWarnings } = dedupeHeaders(headers);
  warnings.push(...headerWarnings);

  const headerMap = headers.reduce<Record<string, string>>((accumulator, rawHeader, index) => {
    accumulator[rawHeader] = normalizedHeaders[index];
    return accumulator;
  }, {});

  const rows: ImportSourceRow[] = limitedFeatures.map((feature, index) => {
    const values = normalizedHeaders.reduce<Record<string, unknown>>((accumulator, header) => {
      accumulator[header] = '';
      return accumulator;
    }, {});

    const properties = feature.properties ?? {};
    for (const [key, value] of Object.entries(properties)) {
      const normalizedHeader = headerMap[key];
      if (normalizedHeader) {
        values[normalizedHeader] = value;
      }
    }

    return {
      index,
      values,
      geometry: feature.geometry ? (feature.geometry as unknown as Record<string, unknown>) : undefined,
    };
  });

  return buildParsedSource(file.name, 'geojson', rows, normalizedHeaders, warnings);
}

function parseXmlDocument(rawText: string): XMLDocument {
  if (typeof DOMParser === 'undefined') {
    throw new Error('El entorno actual no soporta parseo XML para archivos KML');
  }

  const parser = new DOMParser();
  const xmlDocument = parser.parseFromString(rawText, 'application/xml');
  const parserErrors = Array.from(xmlDocument.getElementsByTagName('*')).filter(
    (element) => element.localName === 'parsererror',
  );

  if (parserErrors.length > 0) {
    throw new Error('El archivo KML no es XML válido');
  }

  return xmlDocument;
}

function getElementsByLocalName(root: Document | Element, localName: string): Element[] {
  return Array.from(root.getElementsByTagNameNS('*', localName));
}

function getFirstElementByLocalName(
  root: Document | Element,
  localName: string,
): Element | undefined {
  return getElementsByLocalName(root, localName)[0];
}

function getTrimmedElementText(element?: Element): string | undefined {
  const text = element?.textContent?.trim();
  return text && text.length > 0 ? text : undefined;
}

function getFirstTrimmedText(root: Document | Element, localName: string): string | undefined {
  return getTrimmedElementText(getFirstElementByLocalName(root, localName));
}

function parseKmlCoordinateValue(rawValue: string | undefined): number | string {
  const trimmed = rawValue?.trim() ?? '';
  if (!trimmed) {
    return '';
  }

  const normalized =
    trimmed.includes('.') || !trimmed.includes(',')
      ? trimmed
      : trimmed.replace(',', '.');
  const parsed = Number(normalized);
  return Number.isFinite(parsed) ? parsed : trimmed;
}

function parseKmlPointGeometry(pointElement: Element): Record<string, unknown> {
  const coordinatesText = getFirstTrimmedText(pointElement, 'coordinates') ?? '';
  const firstCoordinateTuple =
    coordinatesText
      .split(/\s+/)
      .map((chunk) => chunk.trim())
      .find((chunk) => chunk.length > 0) ?? '';
  const [rawLng = '', rawLat = ''] = firstCoordinateTuple.split(',');

  return {
    type: 'Point',
    coordinates: [
      parseKmlCoordinateValue(rawLng),
      parseKmlCoordinateValue(rawLat),
    ],
  };
}

function extractKmlGeometry(placemark: Element): Record<string, unknown> | undefined {
  const directChildren = Array.from(placemark.children);
  const directGeometry = directChildren.find((child) =>
    ['Point', 'LineString', 'Polygon', 'MultiGeometry'].includes(child.localName),
  );

  if (!directGeometry) {
    return undefined;
  }

  if (directGeometry.localName === 'Point') {
    return parseKmlPointGeometry(directGeometry);
  }

  if (directGeometry.localName === 'MultiGeometry') {
    const nestedGeometries = Array.from(directGeometry.children).filter((child) =>
      ['Point', 'LineString', 'Polygon'].includes(child.localName),
    );

    if (nestedGeometries.length === 1 && nestedGeometries[0].localName === 'Point') {
      return parseKmlPointGeometry(nestedGeometries[0]);
    }

    return { type: 'MultiGeometry' };
  }

  return { type: directGeometry.localName };
}

function extractKmlExtendedData(placemark: Element): Record<string, unknown> {
  const values: Record<string, unknown> = {};
  const extendedData = getFirstElementByLocalName(placemark, 'ExtendedData');

  if (!extendedData) {
    return values;
  }

  for (const dataElement of getElementsByLocalName(extendedData, 'Data')) {
    const key = dataElement.getAttribute('name')?.trim();
    if (!key) {
      continue;
    }

    const value =
      getFirstTrimmedText(dataElement, 'value') ??
      getFirstTrimmedText(dataElement, 'Value') ??
      getTrimmedElementText(dataElement) ??
      '';
    values[key] = value;
  }

  for (const simpleDataElement of getElementsByLocalName(extendedData, 'SimpleData')) {
    const key = simpleDataElement.getAttribute('name')?.trim();
    if (!key) {
      continue;
    }

    values[key] = getTrimmedElementText(simpleDataElement) ?? '';
  }

  return values;
}

function buildKmlRows(placemarks: Element[]): { headers: string[]; rows: KmlRawRow[] } {
  const rawHeaders: string[] = [];
  const seenHeaders = new Set<string>();

  const registerHeader = (key: string): void => {
    if (!key || seenHeaders.has(key)) {
      return;
    }
    seenHeaders.add(key);
    rawHeaders.push(key);
  };

  const rows = placemarks.map<KmlRawRow>((placemark, index) => {
    const values: Record<string, unknown> = {};

    const name = getFirstTrimmedText(placemark, 'name');
    if (name) {
      values.name = name;
      registerHeader('name');
    }

    const description = getFirstTrimmedText(placemark, 'description');
    if (description) {
      values.description = description;
      registerHeader('description');
    }

    const extendedDataValues = extractKmlExtendedData(placemark);
    for (const [key, value] of Object.entries(extendedDataValues)) {
      if (!(key in values) || isBlank(values[key])) {
        values[key] = value;
      }
      registerHeader(key);
    }

    return {
      index,
      values,
      geometry: extractKmlGeometry(placemark),
    };
  });

  return { headers: rawHeaders, rows };
}

export async function parseKmlImportFile(file: File): Promise<ParsedImportSource> {
  const rawText = await readFileText(file);
  const xmlDocument = parseXmlDocument(rawText);
  const placemarks = getElementsByLocalName(xmlDocument, 'Placemark');

  if (placemarks.length === 0) {
    throw new Error('El archivo KML no contiene Placemarks importables');
  }

  const warnings: string[] = [];
  const limitedPlacemarks = placemarks.slice(0, MAX_ROWS);
  if (placemarks.length > MAX_ROWS) {
    warnings.push(`Se recortaron Placemarks a ${MAX_ROWS}. El backend no acepta más filas por preview.`);
  }

  const { headers, rows: rawRows } = buildKmlRows(limitedPlacemarks);
  const limitedHeaders = headers.slice(0, MAX_COLUMNS);
  if (headers.length > MAX_COLUMNS) {
    warnings.push(`Se recortaron columnas a ${MAX_COLUMNS} para cumplir el límite del backend.`);
  }

  const { headers: normalizedHeaders, warnings: headerWarnings } = dedupeHeaders(limitedHeaders);
  warnings.push(...headerWarnings);

  const headerMap = limitedHeaders.reduce<Record<string, string>>((accumulator, rawHeader, index) => {
    accumulator[rawHeader] = normalizedHeaders[index];
    return accumulator;
  }, {});

  const rows: ImportSourceRow[] = rawRows.map((rawRow) => {
    const values = normalizedHeaders.reduce<Record<string, unknown>>((accumulator, header) => {
      accumulator[header] = '';
      return accumulator;
    }, {});

    for (const [key, value] of Object.entries(rawRow.values)) {
      const normalizedHeader = headerMap[key];
      if (normalizedHeader) {
        values[normalizedHeader] = value;
      }
    }

    return {
      index: rawRow.index,
      values,
      geometry: rawRow.geometry,
    };
  });

  return buildParsedSource(file.name, 'kml', rows, normalizedHeaders, warnings);
}

export async function parseImportFile(file: File): Promise<ParsedImportSource> {
  const lowerName = file.name.toLowerCase();
  if (lowerName.endsWith('.csv')) {
    return parseCsvImportFile(file);
  }

  if (lowerName.endsWith('.geojson') || lowerName.endsWith('.json')) {
    return parseGeoJsonImportFile(file);
  }

  if (lowerName.endsWith('.kml')) {
    return parseKmlImportFile(file);
  }

  throw new Error('Formato no soportado. Usa CSV, GeoJSON o KML.');
}

function findBestColumnMatch(
  field: ImportFieldDefinition,
  columns: SourceColumnMeta[],
  usedColumns: Set<string>,
): string | undefined {
  const candidates = [
    field.key,
    field.targetPath,
    field.label,
    ...field.aliases,
  ].map((candidate) => normalizeText(candidate));

  const exactMatch = columns.find((column) => {
    if (usedColumns.has(column.key)) {
      return false;
    }

    const normalized = normalizeText(column.key);
    return candidates.includes(normalized);
  });

  if (exactMatch) {
    return exactMatch.key;
  }

  const fuzzyMatch = columns.find((column) => {
    if (usedColumns.has(column.key)) {
      return false;
    }

    const normalized = normalizeText(column.key);
    return candidates.some(
      (candidate) =>
        candidate.length >= 3 &&
        (normalized.includes(candidate) || candidate.includes(normalized)),
    );
  });

  return fuzzyMatch?.key;
}

export function buildInitialFieldMappings(
  definition: SerializedImportableEntityDefinition,
  parsedSource: ParsedImportSource,
): FieldMappingState {
  const usedColumns = new Set<string>();

  return definition.fields.reduce<FieldMappingState>((accumulator, field) => {
    const matchedColumn = findBestColumnMatch(field, parsedSource.columns, usedColumns);

    if (matchedColumn) {
      usedColumns.add(matchedColumn);
      accumulator[field.key] = {
        fieldKey: field.key,
        mode: 'column',
        sourceColumnKey: matchedColumn,
      };
      return accumulator;
    }

    accumulator[field.key] = {
      fieldKey: field.key,
      mode: 'ignore',
    };

    return accumulator;
  }, {});
}

function getDistinctSourceValues(
  parsedSource: ParsedImportSource,
  mapping: FieldMappingStateEntry,
  matcher: ImportValueMatcherDefinition,
): string[] {
  if (mapping.mode !== 'column' || !mapping.sourceColumnKey) {
    return [];
  }

  const distinct = new Set<string>();
  for (const row of parsedSource.rows) {
    const rawValue = row.values[mapping.sourceColumnKey];
    if (isBlank(rawValue)) {
      continue;
    }

    if (matcher.strategy === 'split-list-map') {
      const separator = matcher.separator ?? ',';
      stringifyValue(rawValue)
        .split(separator)
        .map((value) => value.trim())
        .filter((value) => value.length > 0)
        .forEach((value) => distinct.add(value));
      continue;
    }

    distinct.add(stringifyValue(rawValue).trim());
  }

  return Array.from(distinct).sort((left, right) => left.localeCompare(right));
}

function autoResolveTargetValue(
  sourceValue: string,
  matcher: ImportValueMatcherDefinition,
): unknown {
  const normalized = normalizeText(sourceValue);

  if (matcher.strategy === 'boolean-map') {
    if (TRUE_VALUES.includes(normalized)) {
      return true;
    }
    if (FALSE_VALUES.includes(normalized)) {
      return false;
    }
    return undefined;
  }

  if (matcher.strategy === 'numeric-scale-map') {
    const normalizedNumber = Number(sourceValue.replace(',', '.'));
    return Number.isFinite(normalizedNumber) ? normalizedNumber : undefined;
  }

  const options = matcher.targetOptions ?? [];
  const matchedOption = options.find((option) => {
    const optionCandidates = [
      option.value,
      option.label,
      ...(option.aliases ?? []),
    ].map((candidate) => normalizeText(candidate));

    return optionCandidates.includes(normalized);
  });

  return matchedOption?.value;
}

export function buildInitialValueMappings(
  definition: SerializedImportableEntityDefinition,
  parsedSource: ParsedImportSource,
  fieldMappings: FieldMappingState,
): ValueMappingState {
  return definition.valueMatchers.reduce<ValueMappingState>((accumulator, matcher) => {
    const fieldMapping = fieldMappings[matcher.fieldKey];
    if (!fieldMapping || fieldMapping.mode !== 'column') {
      return accumulator;
    }

    const distinctValues = getDistinctSourceValues(parsedSource, fieldMapping, matcher);
    accumulator[matcher.fieldKey] = {
      fieldKey: matcher.fieldKey,
      strategy: matcher.strategy,
      separator: matcher.separator,
      mappings: distinctValues.map<ImportValueMappingEntry>((sourceValue) => ({
        sourceValue,
        targetValue: autoResolveTargetValue(sourceValue, matcher),
      })),
    };

    return accumulator;
  }, {});
}

function coerceConstantValue(
  field: ImportFieldDefinition,
  constantValue: unknown,
): unknown {
  if (constantValue === undefined || constantValue === null || constantValue === '') {
    return undefined;
  }

  if (field.dataType === 'number') {
    const numericValue = Number(String(constantValue).replace(',', '.'));
    return Number.isFinite(numericValue) ? numericValue : constantValue;
  }

  if (field.dataType === 'boolean') {
    if (typeof constantValue === 'boolean') {
      return constantValue;
    }

    const normalized = normalizeText(constantValue);
    if (TRUE_VALUES.includes(normalized)) {
      return true;
    }
    if (FALSE_VALUES.includes(normalized)) {
      return false;
    }
  }

  return constantValue;
}

export function serializeFieldMappings(
  definition: SerializedImportableEntityDefinition,
  fieldMappings: FieldMappingState,
): ImportFieldMapping[] {
  return definition.fields.map((field) => {
    const mapping = fieldMappings[field.key] ?? {
      fieldKey: field.key,
      mode: 'ignore',
    };

    return {
      fieldKey: field.key,
      mode: mapping.mode,
      sourceColumnKey: mapping.sourceColumnKey,
      constantValue: mapping.mode === 'constant'
        ? coerceConstantValue(field, mapping.constantValue)
        : undefined,
    };
  });
}

export function serializeValueMappings(
  valueMappings: ValueMappingState,
): ImportValueMapping[] {
  return Object.values(valueMappings)
    .filter((mapping) => mapping.mappings.length > 0)
    .map<ImportValueMapping>((mapping) => ({
      fieldKey: mapping.fieldKey,
      strategy: mapping.strategy,
      separator: mapping.separator,
      mappings: mapping.mappings
        .filter((entry) => entry.sourceValue.trim().length > 0)
        .map((entry) => ({
          sourceValue: entry.sourceValue,
          targetValue: entry.targetValue,
        })),
    }));
}

export function buildSourceSnapshot(
  parsedSource: ParsedImportSource,
  geometryMode: ImportSourceMode,
  coordinateColumns: ImportCoordinateColumns | null,
): ImportSourceSnapshot {
  return {
    fileName: parsedSource.fileName,
    format: parsedSource.format,
    columns: parsedSource.columns.map((column) => ({
      key: column.key,
      label: column.label,
    })),
    rows: parsedSource.rows,
    geometryMode,
    coordinateColumns:
      geometryMode === 'coordinate-columns' && coordinateColumns
        ? coordinateColumns
        : undefined,
  };
}

export function getRequiredFieldMappingErrors(
  definition: SerializedImportableEntityDefinition,
  fieldMappings: FieldMappingState,
): string[] {
  return definition.fields.reduce<string[]>((errors, field) => {
    const mapping = fieldMappings[field.key];
    if (!field.required) {
      return errors;
    }

    if (!mapping || mapping.mode === 'ignore') {
      errors.push(`El campo obligatorio "${field.label}" no está mapeado.`);
      return errors;
    }

    if (mapping.mode === 'column' && !mapping.sourceColumnKey) {
      errors.push(`El campo obligatorio "${field.label}" necesita una columna fuente.`);
    }

    if (
      mapping.mode === 'constant' &&
      (mapping.constantValue === undefined || mapping.constantValue === null || mapping.constantValue === '')
    ) {
      errors.push(`El campo obligatorio "${field.label}" necesita un valor constante.`);
    }

    return errors;
  }, []);
}

export function getRequiredValueMappingErrors(
  definition: SerializedImportableEntityDefinition,
  parsedSource: ParsedImportSource,
  fieldMappings: FieldMappingState,
  valueMappings: ValueMappingState,
): string[] {
  return definition.valueMatchers.reduce<string[]>((errors, matcher) => {
    const fieldMapping = fieldMappings[matcher.fieldKey];
    if (!fieldMapping || fieldMapping.mode !== 'column') {
      return errors;
    }

    if (!matcher.requiredOnUnmatched) {
      return errors;
    }

    const configuredMappings = valueMappings[matcher.fieldKey];
    const expectedSourceValues = getDistinctSourceValues(parsedSource, fieldMapping, matcher);

    const unresolved = expectedSourceValues.filter((sourceValue) => {
      const existingEntry = configuredMappings?.mappings.find(
        (entry) => entry.sourceValue === sourceValue,
      );
      return existingEntry?.targetValue === undefined || existingEntry?.targetValue === '';
    });

    if (unresolved.length > 0) {
      const field = definition.fields.find((candidate) => candidate.key === matcher.fieldKey);
      errors.push(
        `El campo "${field?.label ?? matcher.fieldKey}" tiene ${unresolved.length} valor${unresolved.length === 1 ? '' : 'es'} sin resolver.`,
      );
    }

    return errors;
  }, []);
}

export function getIssueCounts(
  issues: ImportPreviewIssue[],
): Record<ImportPreviewIssue['category'], number> {
  return issues.reduce<Record<ImportPreviewIssue['category'], number>>(
    (accumulator, issue) => {
      accumulator[issue.category] += 1;
      return accumulator;
    },
    {
      fatal: 0,
      error: 0,
      warning: 0,
      default_applied: 0,
      omitted: 0,
    },
  );
}

export function getFieldByKey(
  definition: SerializedImportableEntityDefinition | null,
  fieldKey: string,
): ImportFieldDefinition | undefined {
  return definition?.fields.find((field) => field.key === fieldKey);
}

export function getValueMatcherByFieldKey(
  definition: SerializedImportableEntityDefinition | null,
  fieldKey: string,
): ImportValueMatcherDefinition | undefined {
  return definition?.valueMatchers.find((matcher) => matcher.fieldKey === fieldKey);
}

export function getSourceModeLabel(mode: ImportSourceMode): string {
  return mode === 'feature-geometry'
    ? 'Geometría del archivo'
    : 'Columnas de coordenadas';
}
